<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { DonationReceipt } from '@/types/donationReceipt'
import {
  downloadReceipt,
  isReceiptImage,
  openReceiptForView,
  receiptFileKind,
} from '@/helpers/donationReceipt'

defineProps<{
  receipts: DonationReceipt[]
}>()

const { t } = useI18n()
const previewReceipt = ref<DonationReceipt | null>(null)

const viewReceipt = (receipt: DonationReceipt) => {
  openReceiptForView(receipt, (selected) => {
    previewReceipt.value = selected
  })
}

const closePreview = () => {
  previewReceipt.value = null
}

const onPreviewOverlayClick = (event: MouseEvent) => {
  if (event.target === event.currentTarget) {
    closePreview()
  }
}

const onPreviewKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && previewReceipt.value) {
    closePreview()
  }
}

onMounted(() => {
  document.addEventListener('keydown', onPreviewKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', onPreviewKeydown)
})

const fileKindLabel = (receipt: DonationReceipt) => {
  const kind = receiptFileKind(receipt.filename)
  if (kind === 'pdf') return t('Dashboard.DonationDetailModal.Proof.PdfLabel')
  if (kind === 'image') return t('Dashboard.DonationDetailModal.Proof.ImageLabel')
  return t('Dashboard.DonationDetailModal.Proof.FileLabel')
}

const onDownloadClick = (receipt: DonationReceipt) => {
  downloadReceipt(receipt)
}
</script>

<template>
  <section class="sa-donation-proof" aria-labelledby="donation-proof-heading">
    <h3 id="donation-proof-heading" class="sa-modal-section-heading">
      {{ $t('Dashboard.DonationDetailModal.Proof.Heading') }}
    </h3>

    <ul class="sa-donation-proof__list">
      <li v-for="receipt in receipts" :key="receipt.id" class="sa-donation-proof__item">
        <button
          type="button"
          class="sa-donation-proof__view-btn"
          @click="viewReceipt(receipt)"
        >
          <span
            class="sa-donation-proof__icon"
            :class="`sa-donation-proof__icon--${receiptFileKind(receipt.filename)}`"
            aria-hidden="true"
          >
            <svg
              v-if="receiptFileKind(receipt.filename) === 'pdf'"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
            >
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            <svg
              v-else-if="receiptFileKind(receipt.filename) === 'image'"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <circle cx="8.5" cy="10.5" r="1.5" />
              <path d="M21 17l-5.5-5.5L5 19" />
            </svg>
            <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
              <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z" />
              <polyline points="13 2 13 9 20 9" />
            </svg>
          </span>

          <span class="sa-donation-proof__label">
            <span class="sa-donation-proof__filename">{{ receipt.filename }}</span>
            <span class="sa-donation-proof__kind">{{ fileKindLabel(receipt) }}</span>
          </span>

          <span class="sa-donation-proof__view-text">
            {{ $t('Dashboard.DonationDetailModal.Proof.View') }}
          </span>

          <svg
            class="sa-donation-proof__chev"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clip-rule="evenodd"
            />
          </svg>
        </button>

        <button
          type="button"
          class="sa-donation-proof__download-btn"
          :aria-label="
            $t('Dashboard.DonationDetailModal.Proof.DownloadNamed', {
              filename: receipt.filename,
            })
          "
          @click="onDownloadClick(receipt)"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
              fill-rule="evenodd"
              d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </li>
    </ul>

    <div
      v-if="previewReceipt"
      class="sa-donation-proof-preview"
      role="dialog"
      aria-modal="true"
      :aria-label="
        $t('Dashboard.DonationDetailModal.Proof.OpenFull', {
          filename: previewReceipt.filename,
        })
      "
      @click="onPreviewOverlayClick"
    >
      <div class="sa-donation-proof-preview__panel" @click.stop>
        <div class="sa-donation-proof-preview__hd">
          <p class="sa-donation-proof-preview__caption">{{ previewReceipt.filename }}</p>
          <button
            type="button"
            class="sa-donation-proof-preview__close"
            @click="closePreview"
          >
            {{ $t('Dashboard.DonationDetailModal.Proof.PreviewClose') }}
          </button>
        </div>

        <img
          v-if="isReceiptImage(previewReceipt)"
          :src="previewReceipt.url"
          :alt="previewReceipt.filename"
          class="sa-donation-proof-preview__image"
        />

        <div class="sa-donation-proof-preview__actions">
          <button
            type="button"
            class="sa-donation-proof__download-btn sa-donation-proof__download-btn--labeled"
            :aria-label="
              $t('Dashboard.DonationDetailModal.Proof.DownloadNamed', {
                filename: previewReceipt.filename,
              })
            "
            @click="downloadReceipt(previewReceipt)"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path
                fill-rule="evenodd"
                d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
            <span>{{ $t('Dashboard.DonationDetailModal.Proof.Download') }}</span>
          </button>
          <button type="button" class="sa-btn-primary sa-btn-primary--compact" @click="closePreview">
            {{ $t('Dashboard.DonationDetailModal.Proof.PreviewClose') }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
